'use client';

import type React from 'react';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePlayer } from '../../contexts/PlayerContext';
import type { SoundCloudSeries, SoundCloudTrack } from '../../types';
import { getBasePath } from '../../utils/basePath';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.7); }
  70%  { box-shadow: 0 0 0 12px rgba(0, 255, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0); }
`;

const eqBar = keyframes`
  0%, 100% { height: 4px; }
  50% { height: 16px; }
`;

const Section = styled.section`
  min-height: 100vh;
  background: var(--bg-secondary, #080810);
  padding: 10vh 0 12vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 10;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 8vh 0 9vh;
  }

  @media (max-width: 480px) {
    padding: 7vh 0 8vh;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 6vh;
  animation: ${fadeUp} 0.8s ease both;
  padding: 0 16px;
`;

const SectionLabel = styled.p`
  font-size: 0.85rem;
  color: #ff2d95;
  font-variant-caps: all-petite-caps;
  letter-spacing: 4px;
  margin: 0 0 8px;
  font-weight: 600;

  @media (max-width: 480px) {
    letter-spacing: 2.5px;
    font-size: 0.78rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  margin: 0 0 12px;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: clamp(1.6rem, 8vw, 2.2rem);
    margin: 0 0 10px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary, rgba(255, 255, 255, 0.75));
  font-variant-caps: all-petite-caps;
  margin: 0;
  max-width: 480px;

  @media (max-width: 480px) {
    font-size: 0.86rem;
    max-width: 340px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 4vh;
  padding: 0 20px;

  @media (max-width: 480px) {
    gap: 8px;
    padding: 0 12px;
  }
`;

const FilterChip = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? 'var(--accent-green, #00ff00)' : 'var(--card-bg, rgba(255, 255, 255, 0.05))')};
  color: ${({ $active }) => ($active ? '#000' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))')};
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent-green, #00ff00)' : 'var(--card-border, rgba(255, 255, 255, 0.15))')};
  font-weight: ${({ $active }) => ($active ? '700' : '500')};
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-variant-caps: all-petite-caps;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 480px) {
    padding: 7px 14px;
    font-size: 0.75rem;
  }

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--accent-green, #00ff00)' : 'rgba(255, 45, 149, 0.15)')};
    border-color: ${({ $active }) => ($active ? 'var(--accent-green, #00ff00)' : '#ff2d95')};
    color: var(--text-primary, #fff);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1100px;
  padding: 0 20px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const Card = styled.div<{ $active: boolean }>`
  border-radius: 14px;
  overflow: hidden;
  background: var(--card-bg, rgba(18, 18, 28, 0.95));
  border: 1px solid ${({ $active }) => ($active ? '#00ff00' : 'var(--card-border, rgba(255, 255, 255, 0.1))')};
  box-shadow: ${({ $active }) =>
    $active
      ? '0 0 28px rgba(0, 255, 0, 0.35), 0 8px 32px var(--card-shadow, rgba(0,0,0,0.5))'
      : '0 6px 20px var(--card-shadow, rgba(0,0,0,0.3))'};
  cursor: pointer;
  transition:
    transform 0.3s ease,
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    background-color 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.6s ease both;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ $active }) => ($active ? '#00ff00' : '#ff2d95')};
    box-shadow: 0 12px 36px var(--card-shadow, rgba(0, 0, 0, 0.5));
  }
`;

const CardCover = styled.div<{ $image?: string; $bgColor: string }>`
  width: 100%;
  height: 180px;
  background-color: ${({ $bgColor }) => $bgColor || '#151522'};
  background-image: ${({ $image }) => ($image ? `url(${$image})` : 'none')};
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding: 12px;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(18, 18, 28, 0.9) 100%);
  }
`;

const EqualizerWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 18px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 6px;
  border-radius: 6px;
`;

const EqualizerBar = styled.span<{ $delay: string }>`
  width: 3px;
  background: #00ff00;
  border-radius: 2px;
  animation: ${eqBar} 0.8s ease infinite alternate;
  animation-delay: ${({ $delay }) => $delay};
`;

const CardBody = styled.div`
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: 480px) {
    padding: 14px 14px 16px;
  }
`;

const SeriesBadge = styled.span<{ $series: SoundCloudSeries }>`
  display: inline-block;
  font-size: 0.65rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 2px;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.15);
  z-index: 2;
  position: relative;
  font-weight: 700;
  color: ${({ $series }) => {
    switch ($series) {
      case 'Single / Track':
        return '#00ff00';
      case 'Album / EP':
        return '#ff2d95';
      case 'Live':
        return '#ff5500';
      default:
        return 'rgba(255,255,255,0.85)';
    }
  }};
`;

const CardTitle = styled.h3`
  font-size: 1.15rem;
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  margin: 0 0 6px;
  font-weight: 600;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  font-size: 0.84rem;
  color: var(--text-secondary, rgba(255, 255, 255, 0.75));
  font-variant-caps: all-petite-caps;
  margin: 0 0 16px;
  line-height: 1.5;
  flex: 1;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--text-muted, rgba(255, 255, 255, 0.55));
  font-variant-caps: all-petite-caps;
  margin-top: auto;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PlayButton = styled.button<{ $active: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid ${({ $active }) => ($active ? '#00ff00' : 'rgba(0, 255, 0, 0.6)')};
  background: ${({ $active }) => ($active ? '#00ff00' : 'rgba(0, 255, 0, 0.15)')};
  color: ${({ $active }) => ($active ? '#000' : '#00ff00')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.2s,
    transform 0.2s;
  flex-shrink: 0;
  animation: ${({ $active }) => ($active ? pulse : 'none')} 2s infinite;

  &:hover {
    background: #00ff00;
    color: #000;
    transform: scale(1.1);
  }
`;

const SCProfileLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6vh;
  padding: 11px 26px;
  border: 2px solid #00ff00;
  border-radius: 24px;
  background: #00ff00;
  color: #000000;
  font-variant-caps: all-petite-caps;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(0, 255, 0, 0.25);

  @media (max-width: 480px) {
    margin-top: 5vh;
    padding: 10px 20px;
    font-size: 0.82rem;
  }

  &:hover {
    background: #ff2d95;
    border-color: #ff2d95;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(255, 45, 149, 0.4);
  }
`;

const LoadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4vh;
  padding: 10px 24px;
  background: var(--card-bg, rgba(255, 255, 255, 0.04));
  border: 1px solid var(--card-border, rgba(255, 255, 255, 0.2));
  border-radius: 24px;
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 0.8rem;
  }

  &:hover {
    background: rgba(255, 45, 149, 0.15);
    border-color: #ff2d95;
    color: #ff2d95;
    transform: translateY(-2px);
  }
`;

export interface SoundCloudSectionProps {
  tracks: SoundCloudTrack[];
}

const INITIAL_VISIBLE = 6;
const SERIES_FILTERS: Array<SoundCloudSeries | 'All'> = ['All', 'Album / EP', 'Single / Track'];

/**
 * SoundCloud Mixes & Sets showcase section with pink & green theme and interactive filtering.
 */
export const SoundCloudSection: React.FC<SoundCloudSectionProps> = ({ tracks }) => {
  const basePath = getBasePath();
  const { playTrack, togglePlay, currentTrackUrl, isPlaying } = usePlayer();
  const { requestSoundCloudPermission, preferences } = useConsent();
  const { t } = useLanguage();
  const [selectedFilter, setSelectedFilter] = useState<SoundCloudSeries | 'All'>('All');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const getFilterLabel = (filter: SoundCloudSeries | 'All') => {
    if (filter === 'All') return t('scFilterAll');
    if (filter === 'Album / EP') return t('scFilterAlbum');
    return t('scFilterSingle');
  };

  const filteredTracks =
    selectedFilter === 'All' ? tracks : tracks.filter((track) => track.series === selectedFilter);

  const handleTrackClick = (track: SoundCloudTrack) => {
    if (!preferences.soundCloud) {
      const allowed = requestSoundCloudPermission(track.soundcloudUrl, track.title, () => {
        if (currentTrackUrl === track.soundcloudUrl) {
          togglePlay();
        } else {
          playTrack(track.soundcloudUrl, track.title);
        }
      });
      if (!allowed) return;
    }

    if (currentTrackUrl === track.soundcloudUrl) {
      togglePlay();
    } else {
      playTrack(track.soundcloudUrl, track.title);
    }
  };

  const isCurrent = (track: SoundCloudTrack) => currentTrackUrl === track.soundcloudUrl;
  const isCurrentlyPlaying = (track: SoundCloudTrack) => isCurrent(track) && isPlaying;

  const resolveImageUrl = (imagePath?: string) => {
    if (!imagePath) return undefined;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    if (imagePath.startsWith('/')) return `${basePath}${imagePath}`;
    return `${basePath}/assets/${imagePath}`;
  };

  return (
    <Section id="soundcloud" aria-label="SoundCloud Mixes and Sets">
      <SectionHeader>
        <SectionLabel>{t('scSectionLabel')}</SectionLabel>
        <SectionTitle>{t('scSectionTitle')}</SectionTitle>
        <SectionSubtitle>{t('scSectionSubtitle')}</SectionSubtitle>
      </SectionHeader>

      <FilterContainer role="group" aria-label="Filter SoundCloud series">
        {SERIES_FILTERS.map((filter) => (
          <FilterChip
            key={filter}
            $active={selectedFilter === filter}
            onClick={() => {
              setSelectedFilter(filter);
              setVisibleCount(INITIAL_VISIBLE);
            }}
            aria-pressed={selectedFilter === filter}
          >
            {getFilterLabel(filter)}
          </FilterChip>
        ))}
      </FilterContainer>

      <Grid>
        {filteredTracks.slice(0, visibleCount).map((track) => {
          const active = isCurrent(track);
          const playing = isCurrentlyPlaying(track);
          const imageUrl = resolveImageUrl(track.image);

          return (
            <Card
              key={track.id}
              $active={active}
              onClick={() => handleTrackClick(track)}
              role="button"
              tabIndex={0}
              aria-label={`Play ${track.title}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleTrackClick(track);
              }}
            >
              <CardCover $image={imageUrl} $bgColor={track.accentColor}>
                {playing && (
                  <EqualizerWrapper aria-label="Currently playing">
                    <EqualizerBar $delay="0s" />
                    <EqualizerBar $delay="0.2s" />
                    <EqualizerBar $delay="0.4s" />
                    <EqualizerBar $delay="0.1s" />
                  </EqualizerWrapper>
                )}
                <SeriesBadge $series={track.series}>{track.series}</SeriesBadge>
              </CardCover>

              <CardBody>
                <CardTitle>{track.title}</CardTitle>
                <CardDescription>{track.description}</CardDescription>

                <CardMeta>
                  <MetaItem>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                    </svg>
                    {track.duration ?? track.publishedAt}
                  </MetaItem>

                  <PlayButton
                    $active={active}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrackClick(track);
                    }}
                    aria-label={playing ? `Pause ${track.title}` : `Play ${track.title}`}
                  >
                    {playing ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{ marginLeft: 2 }}
                      >
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    )}
                  </PlayButton>
                </CardMeta>
              </CardBody>
            </Card>
          );
        })}
      </Grid>

      {visibleCount < filteredTracks.length && (
        <LoadMoreButton
          onClick={() => setVisibleCount((c) => Math.min(c + 4, filteredTracks.length))}
          aria-label="Load more tracks"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
          {t('scLoadMore')}
        </LoadMoreButton>
      )}

      <SCProfileLink
        href="https://soundcloud.com/joevnoise"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open full joev noise SoundCloud profile"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 4a8 8 0 0 0-8 8c0 3.3 2 6.1 4.9 7.3.3.1.4-.1.4-.3v-1.1c-2.2.5-2.7-1.1-2.7-1.1-.4-1-1-1.3-1-1.3-.7-.5.1-.5.1-.5.8.1 1.2.8 1.2.8.7 1.2 1.9.9 2.4.7.1-.5.3-.9.5-1.1-1.8-.2-3.7-.9-3.7-4 0-.9.3-1.6.8-2.2 0-.2-.4-1 .1-2.2 0 0 .7-.2 2.3.9.7-.2 1.4-.3 2.1-.3.7 0 1.4.1 2.1.3 1.6-1.1 2.3-.9 2.3-.9.5 1.2.1 2 .1 2.2.5.6.8 1.3.8 2.2 0 3.1-1.9 3.8-3.7 4 .3.3.6.8.6 1.6v2.4c0 .2.1.4.4.3 2.9-1.2 4.9-4 4.9-7.3a8 8 0 0 0-8-8z" />
        </svg>
        more on soundcloud
      </SCProfileLink>
    </Section>
  );
};

export default SoundCloudSection;
